import { useEffect, useState } from "react";
import { LinearProgress } from "@mui/material";
import initSqlJs, { Database } from "sql.js";
import { useRoomDbWebSocket } from "../components/hooks/useRoomDbWebSocket";
import "../assets/css/my.css";
import DSDialog from "./system-design/DSDialog";
import DSDialogTitle from "./system-design/DSDialogTitle";
import DSDialogContent from "./system-design/DSDialogContent";
import DSDialogActions from "./system-design/DSDialogActions";
import DSAlert from "./system-design/DSAlert";
import DSBox from "./system-design/DSBox";
import DSStack from "./system-design/DSStack";
import DSTypography from "./system-design/DSTypography";
import DSButton from "./system-design/DSButton";
import DSPrimaryButton from "./system-design/DSPrimaryButton";
import DSSecondaryButton from "./system-design/DSSecondaryButton";
import {
  DSTable,
  DSTableHead,
  DSTableBody,
  DSTableRow,
  DSTableCell,
} from "./system-design/Table";
import useDeviceType from "../components/hooks/useDeviceType";

interface RoomDbViewerModalProps {
  deviceId: string;
  onClose: () => void;
}

interface TableInfo {
  name: string;
  sql: string;
}

const RoomDbViewerModal: React.FC<RoomDbViewerModalProps> = ({
  deviceId,
  onClose,
}) => {
  const uri = "wss://clock-socket-917257785826.us-central1.run.app/";
  const { start, stop, isConnected, loadingMessage, progress, dbFile, error } =
    useRoomDbWebSocket({ uri, devId: deviceId });

  const { isSmallTablet } = useDeviceType();
  const [db, setDb] = useState<Database | null>(null);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableColumns, setTableColumns] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [customQuery, setCustomQuery] = useState("");
  const [queryResult, setQueryResult] = useState<any[] | null>(null);
  const [queryColumns, setQueryColumns] = useState<string[]>([]);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [dbError, setDbError] = useState<string | null>(null);

  useEffect(() => {
    start();
  }, []);

  useEffect(() => {
    if (dbFile) {
      loadDatabase(dbFile);
    }
  }, [dbFile]);

  const loadDatabase = async (fileData: Uint8Array) => {
    try {
      const SQL = await initSqlJs({
        locateFile: (file) => `https://sql.js.org/dist/${file}`,
      });
      const database = new SQL.Database(fileData);
      setDb(database);

      // Get all tables
      const tablesQuery = database.exec(
        "SELECT name, sql FROM sqlite_master WHERE type='table' ORDER BY name",
      );

      if (tablesQuery.length > 0) {
        const tablesList: TableInfo[] = tablesQuery[0].values.map((row) => ({
          name: row[0] as string,
          sql: row[1] as string,
        }));
        setTables(tablesList);

        if (tablesList.length > 0) {
          setSelectedTable(tablesList[0].name);
          loadTableData(database, tablesList[0].name, 0);
        }
      }
    } catch (err) {
      console.error("Failed to load database:", err);
      setDbError("Database file is corrupted or invalid");
    }
  };

  const loadTableData = (
    database: Database,
    tableName: string,
    page: number,
  ) => {
    try {
      // Get column names
      const columnQuery = database.exec(`PRAGMA table_info(${tableName})`);
      const columns =
        columnQuery[0]?.values.map((row) => row[1] as string) || [];
      setTableColumns(columns);

      // Get data with pagination
      const offset = page * rowsPerPage;
      const dataQuery = database.exec(
        `SELECT * FROM ${tableName} LIMIT ${rowsPerPage} OFFSET ${offset}`,
      );

      if (dataQuery.length > 0) {
        const rows = dataQuery[0].values.map((row) => {
          const rowObj: any = {};
          columns.forEach((col, idx) => {
            rowObj[col] = row[idx];
          });
          return rowObj;
        });
        setTableData(rows);
      } else {
        setTableData([]);
      }
      setCurrentPage(page);
    } catch (err) {
      console.error("Failed to load table data:", err);
      setDbError(`Failed to load table data: ${err}`);
    }
  };

  const handleTableSelect = (tableName: string) => {
    setSelectedTable(tableName);
    setQueryResult(null);
    setQueryError(null);
    if (db) {
      loadTableData(db, tableName, 0);
    }
  };

  const handleExecuteQuery = () => {
    if (!db || !customQuery.trim()) return;

    try {
      setQueryError(null);
      const result = db.exec(customQuery);

      if (result.length > 0) {
        setQueryColumns(result[0].columns);
        const rows = result[0].values.map((row) => {
          const rowObj: any = {};
          result[0].columns.forEach((col, idx) => {
            rowObj[col] = row[idx];
          });
          return rowObj;
        });
        setQueryResult(rows);
      } else {
        setQueryResult([]);
        setQueryColumns([]);
      }
    } catch (err: any) {
      setQueryError(err.message || "Query execution failed");
      setQueryResult(null);
    }
  };

  const handleStop = () => {
    stop();
    onClose();
  };

  const handleDownload = () => {
    if (!dbFile) return;

    // Create a blob from the database file
    const blob = new Blob([dbFile as any], { type: "application/x-sqlite3" });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const a = document.createElement("a");
    a.href = url;
    a.download = `room_db_${deviceId}_${Date.now()}.db`;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderTableData = (data: any[], columns: string[]) => {
    if (data.length === 0) {
      return (
        <DSTypography sx={{ textAlign: "center" }}>
          No data in this table
        </DSTypography>
      );
    }

    return (
      <DSBox sx={{ overflowX: "auto", maxHeight: "400px", overflowY: "auto" }}>
        <DSTable size="small">
          <DSTableHead
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: "background.paper",
            }}
          >
            <DSTableRow>
              {columns.map((col) => (
                <DSTableCell key={col} sx={{ p: 1, fontSize: "12px" }}>
                  {col}
                </DSTableCell>
              ))}
            </DSTableRow>
          </DSTableHead>
          <DSTableBody>
            {data.map((row, idx) => (
              <DSTableRow key={idx} hover>
                {columns.map((col) => (
                  <DSTableCell key={col} sx={{ p: 1, fontSize: "11px" }}>
                    {row[col] !== null ? String(row[col]) : "NULL"}
                  </DSTableCell>
                ))}
              </DSTableRow>
            ))}
          </DSTableBody>
        </DSTable>
      </DSBox>
    );
  };

  const handleRunQuery = () => {
    if (!db) return;

    const defaultQuery = customQuery || "SELECT * FROM sqlite_master LIMIT 10";
    const query = window.prompt(
      "Enter SQL query:\n(Example: SELECT * FROM table_name LIMIT 10)",
      defaultQuery,
    );

    if (query && query.trim()) {
      setCustomQuery(query);
      try {
        setQueryError(null);
        const result = db.exec(query);

        if (result.length > 0) {
          setQueryColumns(result[0].columns);
          const rows = result[0].values.map((row) => {
            const rowObj: any = {};
            result[0].columns.forEach((col, idx) => {
              rowObj[col] = row[idx];
            });
            return rowObj;
          });
          setQueryResult(rows);
        } else {
          setQueryResult([]);
          setQueryColumns([]);
        }
      } catch (err: any) {
        setQueryError(err.message || "Query execution failed");
        setQueryResult(null);
      }
    }
  };

  return (
    <DSDialog
      open
      onClose={handleStop}
      maxWidth="lg"
      fullWidth
      fullScreen={isSmallTablet}
      sx={{ zIndex: 9999 }}
    >
      <DSDialogTitle>Room Database Viewer</DSDialogTitle>
      <DSDialogContent>
        {/* Connection Status */}
        {loadingMessage && (
          <DSBox sx={{ textAlign: "center", mb: 2 }}>
            <DSTypography>{loadingMessage}</DSTypography>
            {progress > 0 && progress < 100 && (
              <DSBox sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: "25px", borderRadius: 2 }}
                />
                <DSTypography variant="caption">{`${progress}%`}</DSTypography>
              </DSBox>
            )}
          </DSBox>
        )}

        {/* Error Messages */}
        {error && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            <strong>Error:</strong> {error}
          </DSAlert>
        )}

        {dbError && (
          <DSAlert
            severity="error"
            onClose={() => setDbError(null)}
            sx={{ mb: 2 }}
          >
            <strong>Database Error:</strong> {dbError}
          </DSAlert>
        )}

        {/* Database Viewer */}
        {db && !loadingMessage && (
          <DSBox
            sx={{
              display: "flex",
              gap: "15px",
              minHeight: "500px",
              flexDirection: { xxs: "column", md: "row" },
            }}
          >
            {/* Left Sidebar - Tables List */}
            <DSBox
              sx={{
                width: { xxs: "100%", md: "200px" },
                borderRight: { md: 1 },
                borderColor: { md: "divider" },
                pr: { md: 2 },
              }}
            >
              <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                Tables ({tables.length})
              </DSTypography>
              <DSBox sx={{ maxHeight: "450px", overflowY: "auto" }}>
                {tables.map((table) => (
                  <DSButton
                    key={table.name}
                    variant={
                      selectedTable === table.name ? "contained" : "outlined"
                    }
                    color={
                      selectedTable === table.name ? "primary" : "secondary"
                    }
                    size="small"
                    fullWidth
                    sx={{ mb: 1, justifyContent: "flex-start" }}
                    onClick={() => handleTableSelect(table.name)}
                  >
                    {table.name}
                  </DSButton>
                ))}
              </DSBox>
            </DSBox>

            {/* Right Panel - Data Viewer */}
            <DSBox sx={{ flex: 1, overflow: "auto" }}>
              {/* Query Interface */}
              <DSBox sx={{ mb: 2 }}>
                <DSPrimaryButton size="small" onClick={handleRunQuery}>
                  Run Custom SQL Query
                </DSPrimaryButton>
                {queryError && (
                  <DSAlert severity="error" sx={{ mt: 1, mb: 0 }}>
                    {queryError}
                  </DSAlert>
                )}
              </DSBox>

              {/* Query Results */}
              {queryResult !== null && (
                <DSBox sx={{ mb: 2 }}>
                  <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                    Query Results ({queryResult.length} rows)
                  </DSTypography>
                  {renderTableData(queryResult, queryColumns)}
                </DSBox>
              )}

              {/* Table Data */}
              {selectedTable && queryResult === null && (
                <DSBox
                  sx={{
                    overflow: "auto",
                  }}
                >
                  <DSStack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                      width: "500px",
                    }}
                  >
                    <DSTypography variant="subtitle2">
                      {selectedTable} ({tableData.length} rows)
                    </DSTypography>
                    <DSStack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center" }}
                    >
                      <DSSecondaryButton
                        size="small"
                        onClick={() => {
                          if (db && selectedTable) {
                            loadTableData(db, selectedTable, currentPage - 1);
                          }
                        }}
                        disabled={currentPage === 0}
                      >
                        Previous
                      </DSSecondaryButton>
                      <DSTypography sx={{ mx: 1 }}>
                        Page {currentPage + 1}
                      </DSTypography>
                      <DSSecondaryButton
                        size="small"
                        onClick={() => {
                          if (db && selectedTable) {
                            loadTableData(db, selectedTable, currentPage + 1);
                          }
                        }}
                        disabled={tableData.length < rowsPerPage}
                      >
                        Next
                      </DSSecondaryButton>
                    </DSStack>
                  </DSStack>
                  {renderTableData(tableData, tableColumns)}
                </DSBox>
              )}
            </DSBox>
          </DSBox>
        )}

        {/* Initial State */}
        {!isConnected && !error && !loadingMessage && (
          <DSBox sx={{ textAlign: "center" }}>
            <DSTypography>
              Click "Start" to connect to the device and retrieve the database.
            </DSTypography>
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions>
        {dbFile && (
          <DSPrimaryButton onClick={handleDownload}>
            Download Database
          </DSPrimaryButton>
        )}
        <DSSecondaryButton onClick={handleStop}>Close</DSSecondaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default RoomDbViewerModal;
